import { Request } from 'api/request';
import type {
    BillingAddressDto,
    BillingClaimScopeDto,
    BillingEraDto,
    BillingProviderDto,
    BillingRenderingProvidersDto,
    BillingServiceLocationDto,
    BillingTaxInfoDto,
    BoardFilters,
    BoardQuery,
    EraListParams,
    KpiQuery,
    TBillingApiScope,
    TOnboardingStepSlug,
} from 'types/insuranceBilling';

const BASE = '/schedule-server/provider/insurance-billing/onboarding';

// 一次性 hydrate 全部 section + onboardingStatus/onboardingStep
export const getBillingOnboarding = () => Request.get({ url: BASE });

export const saveBillingProvider = (dto: BillingProviderDto) =>
    Request.put({ url: `${BASE}/billing-provider`, params: dto });

export const saveBillingTaxInfo = (dto: BillingTaxInfoDto) => Request.put({ url: `${BASE}/tax-info`, params: dto });

export const saveBillingAddress = (dto: BillingAddressDto) => Request.put({ url: `${BASE}/address`, params: dto });

export const saveBillingServiceLocation = (dto: BillingServiceLocationDto) =>
    Request.put({ url: `${BASE}/service-location`, params: dto });

export const saveBillingRenderingProviders = (dto: BillingRenderingProvidersDto) =>
    Request.put({ url: `${BASE}/rendering-providers`, params: dto });

export const saveBillingClaimScope = (dto: BillingClaimScopeDto) =>
    Request.put({ url: `${BASE}/claim-scope`, params: dto });

export const saveBillingEra = (dto: BillingEraDto) => Request.put({ url: `${BASE}/era`, params: dto });

// 记录当前 onboarding 步骤（替代 localStorage）：各 step 保存接口只向前推进，回退到已完成步骤靠本接口
export const saveOnboardingStep = (onboardingStep: TOnboardingStepSlug) =>
    Request.put({ url: `${BASE}/step`, params: { onboardingStep }, disableErrorNote: true });

// Settings 专用改名单入口（与 Step 5 请求体相同）：整表替换 + 给未映射条目建 team 成员
// （PENDING / NOT_INVITED，不发邀请），providerId 回写，不触碰 activate 状态；返回完整聚合 VO。
// 错误码：91028 非 PPA / 91023 名单校验失败（一条不合格两侧都不动）/ 91026 并发锁
export const editRenderingProviders = (dto: BillingRenderingProvidersDto) =>
    Request.put({ url: `${BASE}/edit-rendering-providers`, params: dto });

// 激活 onboarding。业务错误码：91028 非 PPA / 91027 步骤必填缺失 / 91023 名单校验失败 / 91026 并发锁；
// 文案由 handleActivate 按 errorCode 处理
export const activateBillingOnboarding = () => Request.post({ url: `${BASE}/activate` });

// NPPES 查询单个 NPI（只读不落库）；查不到返回 400 → 前端记为 notfound。
// 这条走 AskChart，并顺带校验 owner + spa_api_key，只给已开通 Insurance Billing 的 provider 用
export const lookupNpi = (npi: string) =>
    Request.get({ url: `${BASE}/npi-lookup`, params: { npi }, disableErrorNote: true });

// 独立 NPI 校验通道（eligibility 文档 §3.5）：后端直连 NPPES，不校验 owner、不需要 spa_api_key，
// 任何登录 provider 都能调——给「还没开通 Insurance Billing」的场景用（eligibility 的 NPI 设置流）。
// 出参与上面那条完全一致（后端复用同一份模型），所以两者对 NpiRegistryBox 可互换。
// 业务错误码：91041 = NPPES 查无此 NPI（→ notfound）、1009 = NPPES 不可达
export const lookupNpiStandalone = (npi: string) =>
    Request.get({ url: '/schedule-server/provider/npi-lookup', params: { npi }, disableErrorNote: true });

// Step 5 预检单个 rendering provider email（与 activate 同一套判定；不可用是 200 + addable=false）。
// 团队邀请口径的 /provider/team-member/check-provider-email 不适用本流程——会把本诊所已有医生判为不可用
export const checkRenderingProviderEmail = (email: string) =>
    Request.get({ url: `${BASE}/check-rendering-provider-email`, params: { email }, disableErrorNote: true });

// ---------------- Claims dashboard ----------------
// claims 有两套镜像端点：practice = 全诊所（group 的 Practice view 与 solo 用）；
// my = 名册过滤到调用者本人（仅 group 的 My billing 用）。错误码：91038 调用者不在
// rendering provider 名册（"认不出你是谁"，非权限问题），透传全局错误 toast。
const scopedBase = (scope: TBillingApiScope) =>
    scope === 'my' ? '/schedule-server/provider/insurance-billing/my' : '/schedule-server/provider/insurance-billing';

const ERA_BASE = '/schedule-server/provider/insurance-billing/era';

// claim + visit 混合列表（10/05）：tab / card / 列筛选 / 排序 / 分页全部由服务端处理，
// 出参 Page<TBoardRow>。原 /claims 仍是 claim-only 列表，dashboard 不再使用
export const getBillingBoard = (params: BoardQuery, scope: TBillingApiScope) =>
    Request.get({ url: `${scopedBase(scope)}/claims/board`, params });

// tab 数量、NEEDS ATTENTION、筛选选项（带条数）、首批面板统计
export const getBillingBoardSummary = (params: BoardFilters, scope: TBillingApiScope) =>
    Request.get({ url: `${scopedBase(scope)}/claims/board/summary`, params });

// 还没变成 claim 的 visit 详情；出参 claimId 非 null 时应改开 claim 详情
export const getBillingVisitDetail = (encounterId: number, scope: TBillingApiScope) =>
    Request.get({ url: `${scopedBase(scope)}/claims/visits/${encounterId}` });

// 按服务日期窗口统计（YYYY-MM-DD，含起止日）；不传日期即全量
export const getBillingClaimKpi = (scope: TBillingApiScope, params: KpiQuery = {}) =>
    Request.get({ url: `${scopedBase(scope)}/claims/kpi`, params });

// claim 里出现过的 payer 名；dashboard 的 PAYER 筛选已改用 summary.filters，这里只剩 ERA tab 在用
export const getBillingClaimPayers = (scope: TBillingApiScope) =>
    Request.get({ url: `${scopedBase(scope)}/claims/payers` });

export const getBillingClaimDetail = (id: number, scope: TBillingApiScope) =>
    Request.get({ url: `${scopedBase(scope)}/claims/${id}` });

// 周报；weekStart 必须为周一 YYYY-MM-DD
export const getBillingWeeklyReport = (params: { weekStart: string }, scope: TBillingApiScope) =>
    Request.get({ url: `${scopedBase(scope)}/claims/weekly-report`, params });

// AI 洞察（自然语言）。上游走 agent skill，较慢且可能失败；失败按「暂无洞察」处理，
// 不代表 claim 页不可用 —— 因此不走全局错误 toast
export const getBillingClaimInsight = (params: { from: string; to: string }, scope: TBillingApiScope) =>
    Request.get({ url: `${scopedBase(scope)}/claims/insight`, params, disableErrorNote: true });

// ERA 无法按 provider 拆分（一张支票可能跨多位医生，金额是整张支票合计），只有 practice 级接口，
// 不用 /my 镜像；页面上 My billing 视图不展示 ERA tab
export const getBillingEraList = (params: EraListParams) => Request.get({ url: ERA_BASE, params });

// ERA 汇款单明细（835 文档页）；paymentId 为 ERA 列表行的 paymentId
export const getBillingEraRemittance = (paymentId: number) =>
    Request.get({ url: `${ERA_BASE}/${paymentId}/remittance` });

// ERA PDF 下载链接（S3 预签名短链，900 秒过期，不要缓存）；无 PDF 时返回 400。
// hasPdf=false 的行前端不该调到这里，报错文案由调用方兜底 → 不走全局 toast
export const getBillingEraPdfLink = (paymentId: number) =>
    Request.get({ url: `${ERA_BASE}/${paymentId}/pdf`, disableErrorNote: true });

// Insurance Billing 总状态（绑定、onboarding 进度、canBind/canView 等）。
// eligibility practice 视图带 override：persona（managed/manual）按被查 provider 的 enrollment 判
export const getInsuranceBillingStatus = (overrideProviderId?: number) =>
    Request.get({
        url: '/schedule-server/provider/insurance-billing/status',
        disableErrorNote: true,
        overrideProviderId,
    });

// 开通 Insurance Billing（绑定 AskChart 账号）。幂等可重复调用：局部失败后再点一次即可收敛；
// 仅 owner 本人或代持 owner 的 staff 可调用
export const getStartedInsuranceBilling = () =>
    Request.post({ url: '/schedule-server/provider/insurance-billing/get-started' });

// Treatment plan 编辑器预填（templateCpts + diagnosisHistory + billingCodeHistory）
export const getTreatmentPlanPrefill = (businessType: string, businessId: number, overrideProviderId?: number) =>
    Request.get({
        url: `/schedule-server/provider/treatment-plan/prefill/${businessType}/${businessId}`,
        overrideProviderId,
    });

// "My EHR isn't listed" → 自建/未收录 EHR 提交（Tell us about your EHR 弹窗）。
export const submitCustomEhr = (params: { ehrName: string; loginUrl?: string; note?: string }) =>
    Request.post({
        url: '/schedule-server/provider/insurance-billing/ehr-connections/custom',
        params,
    });

// Client page 诊断弹窗
export const getProspectDiagnosisList = (prospectId: number) =>
    Request.get({ url: `/schedule-server/prospect/diagnosis/list/${prospectId}`, disableErrorNote: true });

export const saveProspectDiagnosis = (
    prospectId: number,
    data: import('types/insuranceBilling').TProspectDiagnosisSaveReq[],
) => Request.post({ url: `/schedule-server/prospect/diagnosis/save/${prospectId}`, params: data });

// ---------------- Payment（onboarding Step 8 / claim settings）；三个接口均 owner-only ----------------
// Square 公开配置。applicationId/locationId 唯一来源是后端（不落前端 env）；
// sandbox / production 由 applicationId 的 sandbox- 前缀判定。
// 91023 = 该环境没配 Square（上游 503），文案由卡表单内联展示 → 不走全局 toast
export const getBillingPaymentConfig = () =>
    Request.get({ url: '/schedule-server/provider/insurance-billing/payment-config', disableErrorNote: true });

// 已存卡列表；空数组是正常态（Step 8 之前本来就没有卡），isDefault=true 即会被扣管理费的卡。
// hydrate 静默使用（非 owner 被拒也不该弹错）→ 不走全局 toast
export const getBillingPaymentMethods = () =>
    Request.get({ url: '/schedule-server/provider/insurance-billing/payment-methods', disableErrorNote: true });

// 存卡。sourceId 是 Square tokenize 的一次性产物（不是卡号），失败必须重新 tokenize，
// 不能拿同一个 sourceId 重试；首张卡上游自动设默认，换卡传 setAsDefault=true 顶替旧默认卡（无需删卡）
export const addBillingPaymentMethod = (params: { sourceId: string; setAsDefault?: boolean }) =>
    Request.post({ url: '/schedule-server/provider/insurance-billing/payment-methods', params });

// 字典搜索（CPT / DIAGNOSIS / POS / MODIFIER）
export const searchBillingCodeConfig = (dictType: string, specialty?: string) =>
    Request.get({ url: '/schedule-server/billing-code-config/search', params: { dictType, specialty } });

// ---------------- Managed billing 开关（Settings › Payment / dashboard Submission off）；三个接口均 owner-only ----------------
// 状态与开关都在 AskChart，三个接口返回同一结构（ManagedBillingVo）
const MANAGED_BILLING = '/schedule-server/provider/insurance-billing/managed-billing';

// 打开 Settings 即拉：失败（非 owner / AskChart 不可用）时区块不展示，不弹全局 toast
export const getManagedBilling = () => Request.get({ url: MANAGED_BILLING, disableErrorNote: true });

export const pauseManagedBilling = (params: import('types/insuranceBilling').ManagedBillingScopeReq) =>
    Request.post({ url: `${MANAGED_BILLING}/pause`, params });

export const resumeManagedBilling = (params: import('types/insuranceBilling').ManagedBillingScopeReq) =>
    Request.post({ url: `${MANAGED_BILLING}/resume`, params });
